/* =============================================================================
   beUNIQ — "View rug in your space" invitation

   The visualiser belongs to an app that injects its own button under a
   generated id (#try-on-inline-… / #try-on-btn-…). This does not reimplement
   any of it: the panel's button forwards the click to the app's own button, so
   there is one implementation to maintain and both entry points behave alike.

   The app renders asynchronously and sometimes after this script runs, so the
   panel stays hidden until a target exists and reveals itself once one does.
   No target, no panel — better than a control that does nothing.
   ========================================================================== */

(() => {
  'use strict'

  // Most specific first: the inline button is the same one the product details
  // area shows, so forwarding to it gives identical behaviour.
  const TARGETS = [
    '[id^="try-on-inline-"] button',
    'button[id^="try-on-btn-"]',
    '[id^="try-on-ctr-"] button',
  ]

  // Only ever a button the shopper could click themselves.
  //
  // There used to be a fallback here that accepted a hidden button when no
  // visible one existed, on the theory that the app might keep its trigger
  // off-screen. It does not. The app ships a hidden prototype
  // (#try-on-ctr-… with display:none) and clones it over each gallery image
  // once its backend confirms the feature is enabled. The fallback matched
  // that prototype, so when the backend was returning 500 and no clone was
  // ever made, this panel revealed itself and offered a button that silently
  // did nothing.
  //
  // Requiring a laid-out element means the panel appears only when the real
  // feature is working, and hides itself when it is not.
  const findAppButton = (self) => {
    for (const sel of TARGETS) {
      for (const el of document.querySelectorAll(sel)) {
        // Never forward to a button inside this panel.
        if (!self.contains(el) && el.offsetParent !== null) return el
      }
    }
    return null
  }

  class BeuniqVisualiser extends HTMLElement {
    connectedCallback() {
      this.cta = this.querySelector('[data-viz-cta]')
      if (!this.cta) return

      // The panel always shows: the image and copy are worth keeping on the
      // page whatever the app is doing. Only the button changes state.
      this.cta.addEventListener('click', (e) => this.forward(e))

      // Re-check when someone shows intent. The backoff below stops after
      // half a minute, but a page can sit open far longer than that while a
      // backend recovers — this keeps the button honest at the only moment it
      // matters, for the cost of one lookup.
      const recheck = () => {
        if (findAppButton(this)) this.setAvailable()
      }
      this.addEventListener('pointerenter', recheck)
      this.addEventListener('focusin', recheck)

      this.watch()
    }

    disconnectedCallback() {
      this.clearTimers()
    }

    /**
     * Look for the app's button on a backoff.
     *
     * Optimistic to start with: the button renders live, so a working app
     * never flashes an "unavailable" state. If the app has still not produced
     * a button by the time a few checks have passed, the button softens and
     * says so. Checks continue after that, so a late recovery re-enables it
     * without a reload.
     *
     * A backoff rather than a MutationObserver: an observer on the body
     * subtree fires on every DOM change, which is costly on a page this busy.
     */
    watch() {
      const schedule = [0, 400, 900, 1800, 3500, 7000, 14000, 28000]
      // Only start claiming unavailability once the app has had a fair chance.
      const settleAfter = 3500

      this.timers = schedule.map((delay) =>
        setTimeout(() => {
          if (findAppButton(this)) this.setAvailable()
          else if (delay >= settleAfter) this.setUnavailable()
        }, delay)
      )
    }

    clearTimers() {
      this.timers?.forEach(clearTimeout)
      this.timers = null
    }

    setAvailable() {
      if (this.available === true) return
      this.available = true
      this.classList.remove('is-unavailable')
      this.cta.removeAttribute('aria-disabled')
    }

    setUnavailable() {
      if (this.available === false) return
      this.available = false
      this.classList.add('is-unavailable')
      // aria-disabled rather than the disabled property: the button stays
      // focusable, so a keyboard user can reach it and hear why it is off.
      this.cta.setAttribute('aria-disabled', 'true')
    }

    forward(event) {
      const target = findAppButton(this)
      if (!target) {
        // Clicked before the backoff settled, or the app went away. Say so
        // rather than appearing to do nothing.
        event?.preventDefault()
        this.setUnavailable()
        return
      }
      this.setAvailable()
      target.click()
    }
  }

  if (!customElements.get('beuniq-visualiser')) {
    customElements.define('beuniq-visualiser', BeuniqVisualiser)
  }
})()
